// Licensed to the Apache Software Foundation (ASF) under one
// or more contributor license agreements.  See the NOTICE file
// distributed with this work for additional information
// regarding copyright ownership.  The ASF licenses this file
// to you under the Apache License, Version 2.0 (the
// "License"); you may not use this file except in compliance
// with the License.  You may obtain a copy of the License at
//
//   http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing,
// software distributed under the License is distributed on an
// "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
// KIND, either express or implied.  See the License for the
// specific language governing permissions and limitations
// under the License.

<template>
  <div class="form-layout" v-ctrl-enter="handleSubmit">
    <span v-if="uploadPercentage > 0">
      <loading-outlined />
      {{ $t('message.upload.file.processing') }}
      <a-progress :percent="uploadPercentage" />
    </span>
    <a-spin :spinning="loading" v-else>
      <a-form
        :ref="formRef"
        :model="form"
        :rules="rules"
        @finish="handleSubmit"
        layout="vertical">
        <a-form-item name="url" ref="url" :label="$t('label.url')">
          <a-input
            v-model:value="form.url"
            :placeholder="apiParams.url.description"/>
        </a-form-item>
        <a-form-item name="name" ref="name">
          <template #label>
            <tooltip-label :title="$t('label.name')" :tooltip="apiParams.name.description"/>
          </template>
          <a-input
            v-model:value="form.name"
            :placeholder="$t('label.volumename')"
            v-focus="true" />
        </a-form-item>
        <a-form-item name="zoneId" ref="zoneId">
          <template #label>
            <tooltip-label :title="$t('label.zoneid')" :tooltip="apiParams.zoneid.description"/>
          </template>
          <infinite-scroll-select
            v-model:value="form.zoneId"
            api="listZones"
            :apiParams="zonesApiParams"
            resourceType="zone"
            optionValueKey="id"
            optionLabelKey="name"
            defaultIcon="global-outlined"
            selectFirstOption="true"
            @change-option-value="handleZoneChange" />
        </a-form-item>
        <a-form-item name="format" ref="format">
          <template #label>
            <tooltip-label :title="$t('label.format')" :tooltip="apiParams.format.description"/>
          </template>
          <a-select
            v-model:value="form.format"
            showSearch
            optionFilterProp="value"
            :filterOption="(input, option) => {
              return option.value.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }" >
            <a-select-option v-for="format in formats" :key="format">
              {{ format }}
            </a-select-option>
          </a-select>
        </a-form-item>
        <a-form-item name="diskofferingid" ref="diskofferingid">
          <template #label>
            <tooltip-label :title="$t('label.diskofferingid')" :tooltip="apiParams.diskofferingid.description || $t('label.diskoffering')"/>
          </template>
          <infinite-scroll-select
            v-model:value="form.diskofferingid"
            api="listDiskOfferings"
            :apiParams="diskOfferingsApiParams"
            resourceType="diskoffering"
            optionValueKey="id"
            optionLabelKey="displaytext"
            defaultIcon="hdd-outlined"
            :defaultOption="{ id: null, displaytext: ''}"
            allowClear="true"
            @change-option="onChangeDiskOffering" />
        </a-form-item>
        <a-form-item name="checksum" ref="checksum">
          <template #label>
            <tooltip-label :title="$t('label.volumechecksum')" :tooltip="apiParams.checksum.description"/>
          </template>
          <a-input
            v-model:value="form.checksum"
            :placeholder="$t('label.volumechecksum.description')"
          />
        </a-form-item>
        <a-form-item name="domainid" ref="domainid" v-if="'listDomains' in $store.getters.apis">
          <template #label>
            <tooltip-label :title="$t('label.domain')" :tooltip="apiParams.domainid.description"/>
          </template>
          <infinite-scroll-select
            v-model:value="form.domainid"
            api="listDomains"
            :apiParams="domainsApiParams"
            resourceType="domain"
            optionValueKey="id"
            optionLabelKey="path"
            defaultIcon="block-outlined"
            allowClear="true"
            :placeholder="$t('label.domainid')"
            @change-option-value="handleDomainChange" />
        </a-form-item>
        <a-form-item name="account" ref="account" v-if="'listDomains' in $store.getters.apis">
          <template #label>
            <tooltip-label :title="$t('label.account')" :tooltip="apiParams.account.description"/>
          </template>
          <infinite-scroll-select
            v-model:value="form.account"
            api="listAccounts"
            :apiParams="accountsApiParams"
            resourceType="account"
            optionValueKey="name"
            optionLabelKey="name"
            defaultIcon="team-outlined"
            :placeholder="$t('label.account')"
            allowClear="true"
            @change-option-value="handleAccountChange" />
        </a-form-item>
        <div :span="24" class="action-button">
          <a-button @click="closeAction">{{ $t('label.cancel') }}</a-button>
          <a-button :loading="loading" type="primary" ref="submit" @click="handleSubmit">{{ $t('label.ok') }}</a-button>
        </div>
      </a-form>
    </a-spin>
  </div>
</template>

<script>
import { ref, reactive, toRaw } from 'vue'
import { api } from '@/api'
import { mixinForm } from '@/utils/mixin'
import ResourceIcon from '@/components/view/ResourceIcon'
import TooltipLabel from '@/components/widgets/TooltipLabel'
import InfiniteScrollSelect from '@/components/widgets/InfiniteScrollSelect.vue'

export default {
  name: 'UploadVolume',
  mixins: [mixinForm],
  components: {
    ResourceIcon,
    TooltipLabel,
    InfiniteScrollSelect
  },
  data () {
    return {
      formats: ['RAW', 'VHD', 'VHDX', 'OVA', 'QCOW2'],
      zoneSelected: '',
      selectedDiskOfferingId: null,
      domainId: null,
      account: null,
      uploadParams: null,
      customDiskOffering: false,
      isCustomizedDiskIOps: false,
      loading: false,
      uploadPercentage: 0
    }
  },
  beforeCreate () {
    this.apiParams = this.$getApiParams('uploadVolume')
  },
  computed: {
    zonesApiParams () {
      return {
        showicon: true
      }
    },
    diskOfferingsApiParams () {
      if (!this.form.zoneId) {
        return null
      }
      return {
        zoneid: this.form.zoneId,
        listall: true
      }
    },
    domainsApiParams () {
      return {
        listall: true,
        details: 'min'
      }
    },
    accountsApiParams () {
      return {
        domainid: this.form?.domainid || null,
        showicon: true
      }
    }
  },
  created () {
    this.initForm()
  },
  methods: {
    initForm () {
      this.formRef = ref()
      this.form = reactive({
        format: this.formats[0]
      })
      this.rules = reactive({
        url: [{ required: true, message: this.$t('message.error.required.input') }],
        name: [{ required: true, message: this.$t('message.error.volume.name') }],
        zoneId: [{ required: true, message: this.$t('message.error.select') }],
        format: [{ required: true, message: this.$t('message.error.select') }]
      })
    },
    handleZoneChange (zoneId) {
      this.form.zoneId = zoneId
      // InfiniteScrollSelect will auto-reload disk offerings when apiParams changes
    },
    onChangeDiskOffering (offering) {
      if (offering) {
        this.customDiskOffering = offering.iscustomized || false
        this.isCustomizedDiskIOps = offering.iscustomizediops || false
      } else {
        this.customDiskOffering = false
        this.isCustomizedDiskIOps = false
      }
    },
    handleDomainChange (domainId) {
      this.form.domainid = domainId
      this.domainId = domainId
      this.form.account = null
    },
    handleAccountChange (accountName) {
      this.form.account = accountName
      this.account = accountName
    },
    handleSubmit (e) {
      e.preventDefault()
      if (this.loading) return
      this.formRef.value.validate().then(() => {
        const formRaw = toRaw(this.form)
        const values = this.handleRemoveFields(formRaw)
        const params = {}
        for (const key in values) {
          const input = values[key]
          if (input === undefined) {
            continue
          }
          params[key] = input
        }
        params.domainId = this.domainId
        this.loading = true
        api('uploadVolume', params).then(json => {
          this.$notification.success({
            message: this.$t('message.success.upload'),
            description: this.$t('message.success.upload.volume.description')
          })
          this.closeAction()
          this.$emit('refresh-data')
        }).catch(error => {
          this.$notifyError(error)
        }).finally(() => {
          this.loading = false
        })
      }).catch((error) => {
        this.formRef.value.scrollToField(error.errorFields[0].name)
      })
    },
    closeAction () {
      this.$emit('close-action')
    }
  }
}
</script>

<style scoped lang="less">
.form-layout {
  width: 80vw;

  @media (min-width: 700px) {
    width: 550px;
  }
}
</style>
